import { ImdbMovieData } from '@/types/model';

export interface ImdbTVShowData {
  type: 'series';
  primaryTitle: string;
  primaryImage: { url: string; width: number; height: number };
  startYear: number;
  endYear?: number;
  stars: Array<{ id: string; displayName: string; primaryImage: { url: string; width: number; height: number } }>;
  rating: { aggregateRating: number; voteCount: number };
  plot: string;
  runtimeSeconds?: number;
  genres: string[];
  creators?: Array<{ displayName: string }>;
  totalSeasons?: number;
  totalEpisodes?: number;
  originCountries: { code: string, name: string }[];
}

export interface ImdbEpisodeData {
  type: 'tvEpisode';
  id?: string;
  primaryTitle: string;
  primaryImage: { url: string; width: number; height: number };
  startYear?: number;
  rating: { aggregateRating: number; voteCount: number };
  plot: string;
  runtimeSeconds?: number;
  seasonNumber?: number;
  episodeNumber?: number;
  parentTvSeries?: {
    id: string;
    primaryTitle: string;
  };
}

const IMDB_API_BASE_URL = 'https://api.imdbapi.dev';

export interface ImdbApiError {
  error: string;
  message: string;
}

export const imdbApiService = {
  /**
   * Get US-based classification (certificate) for a title and map to app enums.
   * Returns a mapped classification string or null when not found.
   */
  async getClassificationByImdbId(
    imdbId: string,
    titleType: 'movie' | 'series'
  ): Promise<string | null> {
    const cleanImdbId = imdbId.replace(/^tt/i, '');

    type ImdbCertificate = {
      rating?: string;
      country?: { code?: string; name?: string };
      attributes?: string[];
    };

    const normalize = (s?: string) => (s || '')
      .toUpperCase()
      .replace(/_/g, '-')
      .replace(/\s+/g, '')
      .trim();

    const mapForMovie = (rating?: string): string | null => {
      const r = normalize(rating);
      const direct: Record<string, string> = {
        'G': 'G',
        'PG': 'PG',
        'PG-13': 'PG_13',
        'PG13': 'PG_13',
        'R': 'R',
        'NC-17': 'NC_17',
        'NC17': 'NC_17',
        'X': 'NC_17',
        'UNRATED': 'NR',
        'NOTRATED': 'NR',
        'NA': 'NR',
        'N/A': 'NR'
      };
      return direct[r] || null;
    };

    const mapForSeries = (rating?: string): string | null => {
      const r = normalize(rating);
      const direct: Record<string, string> = {
        'TV-14': 'TV_14',
        'TV14': 'TV_14',
        'TV-MA': 'TV_MA',
        'TVMA': 'TV_MA',
        'TV-PG': 'PG',
        'TVPG': 'PG',
        'TV-G': 'G',
        'TVG': 'G',
        'TV-Y': 'G',
        'TVY': 'G',
        'TV-Y7': 'PG',
        'TVY7': 'PG',
        // Sometimes series can have film-style ratings as a proxy
        'PG-13': 'PG_13',
        'PG13': 'PG_13',
        'PG': 'PG',
        'G': 'G',
        'UNRATED': 'NR',
        'NOTRATED': 'NR',
        'NA': 'NR',
        'N/A': 'NR'
      };
      return direct[r] || null;
    };

    try {
      const response = await fetch(`${IMDB_API_BASE_URL}/titles/tt${cleanImdbId}/certificates`, {
        method: 'GET',
        headers: { 'Content-Type': 'application/json' },
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      const certificates: ImdbCertificate[] = data?.certificates || [];
      if (!Array.isArray(certificates) || certificates.length === 0) return null;

      const mapFn = titleType === 'movie' ? mapForMovie : mapForSeries;

      // Prefer US entries first
      const usFirst = [
        ...certificates.filter(c => c?.country?.code === 'US'),
        ...certificates.filter(c => c?.country?.code !== 'US')
      ];

      for (const cert of usFirst) {
        const mapped = mapFn(cert.rating);
        if (mapped) return mapped;
      }

      // Fallback when nothing is mapped
      return null;
    } catch (error) {
      if (error instanceof Error) {
        throw new Error(`Failed to fetch classification: ${error.message}`);
      }
      throw new Error('Failed to fetch classification: Unknown error');
    }
  },
  /**
   * Search movie by IMDB ID
   */
  async getMovieByImdbId(imdbId: string): Promise<ImdbMovieData> {
    // Clean the IMDB ID (remove 'tt' prefix if present)
    const cleanImdbId = imdbId.replace(/^tt/i, '');

    try {
      const response = await fetch(`${IMDB_API_BASE_URL}/titles/tt${cleanImdbId}`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      return await response.json();
    } catch (error) {
      if (error instanceof Error) {
        throw new Error(`Failed to fetch movie data: ${error.message}`);
      }
      throw new Error('Failed to fetch movie data: Unknown error');
    }
  },

  /**
   * Get episodes list for a TV show by IMDB ID (supports pagination via pageToken)
   */
  async getEpisodesBySeriesImdbId(
    seriesImdbId: string,
    pageToken?: string
  ): Promise<{ episodes: Array<{ id?: string; season?: string; episodeNumber?: number }>; nextPageToken?: string }>
  {
    const cleanSeriesId = seriesImdbId.replace(/^tt/i, '');
    const query = pageToken ? `?pageToken=${encodeURIComponent(pageToken)}` : '';
    try {
      const response = await fetch(`${IMDB_API_BASE_URL}/titles/tt${cleanSeriesId}/episodes${query}`, {
        method: 'GET',
        headers: { 'Content-Type': 'application/json' },
      });
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      const data = await response.json();
      return {
        episodes: (data?.episodes || []) as Array<{ id?: string; season?: string; episodeNumber?: number }>,
        nextPageToken: data?.nextPageToken || undefined,
      };
    } catch (error) {
      if (error instanceof Error) {
        throw new Error(`Failed to fetch episodes list: ${error.message}`);
      }
      throw new Error('Failed to fetch episodes list: Unknown error');
    }
  },

  /**
   * Iterate the episodes list to find a given episode within a TV series and return season/episode numbers.
   */
  async findEpisodePositionInSeries(
    seriesImdbId: string,
    episodeImdbId: string
  ): Promise<{ seasonNumber?: number; episodeNumber?: number } | null> {
    const targetId = episodeImdbId.replace(/^tt/i, '');
    let pageToken: string | undefined = undefined;
    while (true) {
      const { episodes, nextPageToken } = await this.getEpisodesBySeriesImdbId(seriesImdbId, pageToken);
      const found = episodes.find(e => (e.id || '').replace(/^tt/i, '') === targetId);
      if (found) {
        const seasonNumber = found.season ? parseInt(found.season, 10) : undefined;
        const episodeNumber = typeof found.episodeNumber === 'number' ? found.episodeNumber : undefined;
        return { seasonNumber, episodeNumber };
      }
      if (!nextPageToken) break;
      pageToken = nextPageToken;
    }
    return null;
  },

  async getTitles(imdbId: string): Promise<Array<{
    text?: string;
    country?: { code?: string; name?: string };
    language?: { code?: string; name?: string };
    attributes?: string[];
  }>> {
    const cleanImdbId = imdbId.replace(/^tt/i, '');

    try {
      const response = await fetch(`${IMDB_API_BASE_URL}/titles/tt${cleanImdbId}/akas`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();

      const akas: Array<{
        text?: string;
        country?: { code?: string; name?: string };
        language?: { code?: string; name?: string };
        attributes?: string[];
      }> = data?.akas || [];

      return akas;
    } catch (error) {
      if (error instanceof Error) {
        throw new Error(`Failed to fetch titles data: ${error.message}`);
      }
      throw new Error('Failed to fetch titles data: Unknown error');
    }
  },

  /**
   * Get release date by IMDB ID. Prefers US release date if available.
   * Returns ISO string in format YYYY-MM-DD or null if not found.
   */
  async getReleaseDateByImdbId(imdbId: string): Promise<string | null> {
    const cleanImdbId = imdbId.replace(/^tt/i, '');

    try {
      const response = await fetch(`${IMDB_API_BASE_URL}/titles/tt${cleanImdbId}/releaseDates`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();

      const releaseDates: Array<{
        country?: { code?: string; name?: string };
        releaseDate?: { year?: number; month?: number; day?: number };
        attributes?: string[];
      }> = data?.releaseDates || [];

      if (!Array.isArray(releaseDates) || releaseDates.length === 0) return null;

      // Prefer US release date
      const us = releaseDates.find((r) => r?.country?.code === 'US');
      const chosen = us || releaseDates[0];
      const rd = chosen?.releaseDate;
      if (!rd?.year || !rd?.month || !rd?.day) return null;

      const pad = (n: number) => String(n).padStart(2, '0');
      return `${rd.year}-${pad(rd.month)}-${pad(rd.day)}`;
    } catch (error) {
      if (error instanceof Error) {
        throw new Error(`Failed to fetch release date: ${error.message}`);
      }
      throw new Error('Failed to fetch release date: Unknown error');
    }
  },

  /**
   * Search TV show by IMDB ID
   */
  async getTVShowByImdbId(imdbId: string): Promise<ImdbTVShowData> {
    const cleanImdbId = imdbId.replace(/^tt/i, '');

    try {
      const response = await fetch(`${IMDB_API_BASE_URL}/titles/tt${cleanImdbId}`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      return await response.json();
    } catch (error) {
      if (error instanceof Error) {
        throw new Error(`Failed to fetch TV show data: ${error.message}`);
      }
      throw new Error('Failed to fetch TV show data: Unknown error');
    }
  },

  /**
   * Search episode by IMDB ID
   */
  async getEpisodeByImdbId(imdbId: string): Promise<ImdbEpisodeData> {
    const cleanImdbId = imdbId.replace(/^tt/i, '');

    try {
      const response = await fetch(`${IMDB_API_BASE_URL}/titles/tt${cleanImdbId}`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      return await response.json();
    } catch (error) {
      if (error instanceof Error) {
        throw new Error(`Failed to fetch episode data: ${error.message}`);
      }
      throw new Error('Failed to fetch episode data: Unknown error');
    }
  },

  /**
   * Search movies by title
   */
  async searchMoviesByTitle(title: string, year?: string): Promise<ImdbMovieData[]> {
    try {
      const params = new URLSearchParams({
        s: title,
        type: 'movie',
      });

      if (year) {
        params.append('y', year);
      }

      const response = await fetch(`${IMDB_API_BASE_URL}/search/?${params}`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();

      if (data.Response === 'False' || data.Error) {
        throw new Error(data.Error || 'No movies found');
      }

      return data.Search || [];
    } catch (error) {
      if (error instanceof Error) {
        throw new Error(`Failed to search movies: ${error.message}`);
      }
      throw new Error('Failed to search movies: Unknown error');
    }
  },
};
